import { useState, useEffect } from "react";
import { parseTotalDuration } from "src/utils";

export const useFlightFilters = (isRoundTrip) => {
    const [flightsData, setFlightsData] = useState({
        offers: [],
        loading: true,
    });

    const [airlines, setAirlines] = useState([]);

    const [airlinesCodes, setAirlinesCodes] = useState([]);

    const [originalData, SetOriginalData] = useState([]);

    const [showSnackBarAfterFilter, setShowSnackBarAfterFilter] =
        useState(false);

    const [filters, setFilters] = useState({
        airlines: [],
        segments: 0,
        duration: "default",
        price: "default",
        departureTime: {
            value: [null, null],
            changed: false,
        },
        arrivalTime: {
            value: [null, null],
            changed: false,
        },
        departureReturnTime: {
            value: [null, null],
            changed: false,
        },
        arrivalReturnTime: {
            value: [null, null],
            changed: false,
        },
        minDuration: 0,
        maxDuration: 1440,
        loyaltyCards: [],
    });

    useEffect(() => {
        if (originalData?.length <= 0) return;
        getUniqueAirlines();
    }, [originalData]);

    const getUniqueAirlines = () => {
        const airlineCod = [];
        const airlinesList = originalData?.flatMap((offer) =>
            offer?.slice?.segments?.map((segment) => {
                const airlineCode =
                    offer?.gdsprovider === "duffel"
                        ? segment?.marketing_carrier?.iata_code
                        : segment?.Segment?.MarketingCarriercode;

                return airlineCode;
            }),
        );

        const uniquedCodes = [...new Set(airlinesList)];

        const uniqueData = uniquedCodes?.map((code) => {
            const airlineName = originalData
                ?.map((offer) => offer.slice)
                ?.flatMap((slice) => slice.segments)
                ?.find(
                    (segment) => segment.marketing_carrier?.iata_code === code,
                );

            return {
                code: code,
                name: airlineName?.marketing_carrier?.name ?? code,
            };
        });

        setAirlinesCodes(uniqueData);
        setAirlines([
            ...new Set(
                uniqueData?.map((airline) => airline?.name ?? airline?.code),
            ),
        ]);
    };

    const handleSetFilter = ({ target }) => {
        const { name, value } = target;

        setFilters({
            ...filters,
            [name]: value,
        });
    };

    const clearValues = (setValues = true) => {
        setFilters({
            airlines: [],
            segments: 0,
            duration: "default",
            price: "default",
            departureTime: {
                value: [null, null],
                changed: false,
            },
            arrivalTime: {
                value: [null, null],
                changed: false,
            },
            departureReturnTime: {
                value: [null, null],
                changed: false,
            },
            arrivalReturnTime: {
                value: [null, null],
                changed: false,
            },
            minDuration: 0,
            maxDuration: 1440,
            loyaltyCards: [],
        });

        if (!setValues) return;

        setFlightsData({
            offers: originalData,
            loading: false,
            offersWithoutSlice: originalData,
        });
    };

    const handleFilter = () => {
        setFlightsData({
            offers: [],
            loading: true,
        });

        let offers = [...originalData];

        offers = filterByDates(offers, "departing");

        offers = filterByDates(offers, "returning");

        offers = filterBySegments(offers);

        offers = filterByDuration(offers);

        offers = filterByAirlines(offers);

        offers = filterByLoyaltyCards(offers);

        offers = sortOffers(offers);

        setFlightsData({
            offers,
            loading: false,
            offersWithoutSlice: offers,
        });

        setShowSnackBarAfterFilter(offers?.length <= 0);
    };

    // const onChangePage = (page, BoxRef, type) => {
    //     if (BoxRef?.current) {
    //         if (type === "into-view") {
    //             BoxRef.current.scrollIntoView({
    //                 behavior: "smooth",
    //                 block: "start",
    //             });
    //         } else {
    //             const element = BoxRef.current;
    //             const topPosition =
    //                 element.getBoundingClientRect().top + window.scrollY - 180;
    //             window.scrollTo({ top: topPosition, behavior: "smooth" });
    //         }
    //     } else {
    //         window.scrollTo({ top: 0, behavior: "smooth" });
    //     }
    //     setFlightsData({
    //         ...flightsData,
    //         offers: flightsData?.offersWithoutSlice
    //     });
    // };

    const filterByLoyaltyCards = (offers) => {
        if (filters.loyaltyCards.length === 0) return offers;

        const selectedLoyaltyAirlineCodes = filters.loyaltyCards.map(
            (card) => card.airline.iata_code,
        );

        return offers.filter((offer) =>
            offer?.airlines?.some((airlineCode) =>
                selectedLoyaltyAirlineCodes.includes(airlineCode),
            ),
        );
    };

    const filterByDuration = (offers) => {
        return [...offers]?.filter(
            (offer) =>
                offer?.totalDuration >= filters.minDuration &&
                offer?.totalDuration <= filters.maxDuration,
        );
    };

    const filterBySegments = (offers) => {
        const maxSegments = !isNaN(parseInt(filters.segments))
            ? parseInt(filters.segments) + 1
            : 1;
        return offers.filter((offer) => {
            if (isRoundTrip) {
                return offer.slice?.segments.length <= maxSegments;
            }
            return offer.slice?.segments.length <= maxSegments;
        });
    };

    const filterByDates = (offers, type) => {
        return [...offers]?.filter((offer) => {
            const value = offer.gdsprovider === "duffel" ? "slice" : "slice";
            const sliceSelected = offer?.[value];

            if (!sliceSelected) return false;

            const departingDate =
                offer.gdsprovider === "duffel"
                    ? sliceSelected?.segments[0]?.departing_at
                    : sliceSelected?.segments[0]?.Segment?.DepartureDateTime;
            const lastSegment =
                sliceSelected?.segments[sliceSelected?.segments?.length - 1];
            const arrivalDate =
                offer.gdsprovider === "duffel"
                    ? lastSegment?.arriving_at
                    : lastSegment?.Segment?.ArrivalDateTime;

            if (!departingDate || !arrivalDate) return false;

            const [departingHours, departingMinutes] = departingDate
                .split("T")[1]
                .split(":")
                .map(Number);
            const [arrivalHours, arrivalMinutes] = arrivalDate
                .split("T")[1]
                .split(":")
                .map(Number);

            const [departureFilter, arrivalFilter] =
                type === "departing"
                    ? ["departureTime", "arrivalTime"]
                    : ["departureReturnTime", "arrivalReturnTime"];

            if (filters[departureFilter].changed) {
                const [minDepartureFilter, maxDepartureFilter] =
                    filters[departureFilter].value;

                if (minDepartureFilter) {
                    const minDepartureMinutes = minDepartureFilter.getMinutes();
                    const minDepartureHours = minDepartureFilter.getHours();

                    if (
                        departingHours < minDepartureHours ||
                        (departingHours === minDepartureHours &&
                            departingMinutes < minDepartureMinutes)
                    ) {
                        return false;
                    }
                }

                if (maxDepartureFilter) {
                    const maxDepartureMinutes = maxDepartureFilter.getMinutes();
                    const maxDepartureHours = maxDepartureFilter.getHours();

                    if (
                        departingHours > maxDepartureHours ||
                        (departingHours === maxDepartureHours &&
                            departingMinutes > maxDepartureMinutes)
                    ) {
                        return false;
                    }
                }
            }

            if (filters[arrivalFilter].changed) {
                const [minArrivalFilter, maxArrivalFilter] =
                    filters[arrivalFilter].value;

                if (minArrivalFilter) {
                    const minArrivingMinutes = minArrivalFilter.getMinutes();
                    const minArrivingHours = minArrivalFilter.getHours();

                    if (
                        arrivalHours < minArrivingHours ||
                        (arrivalHours === minArrivingHours &&
                            arrivalMinutes < minArrivingMinutes)
                    ) {
                        return false;
                    }
                }

                if (maxArrivalFilter) {
                    const maxArrivingMinutes = maxArrivalFilter.getMinutes();
                    const maxArrivingHours = maxArrivalFilter.getHours();

                    if (
                        arrivalHours > maxArrivingHours ||
                        (arrivalHours === maxArrivingHours &&
                            arrivalMinutes > maxArrivingMinutes)
                    ) {
                        return false;
                    }
                }
            }

            return true;
        });
    };

    const sortOffers = (offers) => {
        return [...offers].sort((a, b) => {
            if (filters.price !== "default") {
                const priceA = parseFloat(
                    a?.gdsprovider === "duffel"
                        ? a?.total_amount
                        : a?.totalPrice,
                );
                const priceB = parseFloat(
                    b?.gdsprovider === "duffel"
                        ? b?.total_amount
                        : b?.totalPrice,
                );
                const priceDiff =
                    filters.price === "high"
                        ? priceB - priceA
                        : priceA - priceB;
                if (priceDiff !== 0) return priceDiff;
            }

            if (filters.duration !== "default") {
                const durationDiff =
                    filters.duration === "high"
                        ? b?.totalDuration - a?.totalDuration
                        : a?.totalDuration - b?.totalDuration;
                if (durationDiff !== 0) return durationDiff;
            }

            return a?.totalSegments - b?.totalSegments;
        });
    };

    const filterByAirlines = (offers) => {
        if (filters.airlines.length === 0) return offers;

        return [...offers]?.filter((offer) =>
            offer?.airlines?.some((airline) => {
                const airlineCode = airlinesCodes?.find(
                    (air) => air?.code === airline,
                );
                return (
                    filters.airlines.includes(airline) ||
                    (airlineCode &&
                        filters.airlines.includes(airlineCode?.name))
                );
            }),
        );
    };

    const getOffersWithTotalValues = (offers) => {
        return offers?.map((offer) => {
            const airlines = [];
            let totalSegments = 0;
            let totalDurationInMinutes;

            if (offer.gdsprovider === "duffel") {
                const durations = [];
                const slice = offer?.slice;
                if (slice) {
                    slice?.segments?.forEach((segment) => {
                        if (segment?.duration) {
                            durations.push(segment.duration);
                        }
                        const code =
                            segment?.marketing_carrier?.iata_code ||
                            segment?.carrierCode;
                        airlines.push(code);
                    });
                    totalSegments += slice?.segments?.length || 0;
                }

                const totalDurationStr = parseTotalDuration(durations);
                const [hoursStr, , minutesStr] = totalDurationStr.split(" ");
                totalDurationInMinutes =
                    parseInt(hoursStr) * 60 + parseInt(minutesStr);
            }
            if (offer.gdsprovider === "mystifly") {
                let totalDuration = 0;
                const slice = offer?.slice;
                if (slice) {
                    slice?.segments?.forEach((segment) => {
                        if (segment.Segment.JourneyDuration) {
                            totalDuration += segment.Segment.JourneyDuration;
                        }
                        const code =
                            segment.Segment.MarketingCarriercode ??
                            segment.Segment.OperatingCarrierCode;
                        airlines.push(code);
                    });
                    totalSegments += slice?.segments?.length || 0;
                }

                totalDurationInMinutes = totalDuration;
            }

            return {
                ...offer,
                totalDuration: totalDurationInMinutes,
                totalSegments,
                airlines: [...new Set(airlines)],
            };
        });
    };

    return {
        flightsData,
        originalData,
        setFlightsData,
        SetOriginalData,
        airlines,
        filters,
        handleSetFilter,
        getOffersWithTotalValues,
        clearValues,
        handleFilter,
        showSnackBarAfterFilter,
    };
};
